import {
  Accordion,
  Alert,
  Anchor,
  Badge,
  Card,
  Code,
  Divider,
  Group,
  List,
  SimpleGrid,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  rem,
} from "@mantine/core";
import { createStyles } from "@mantine/emotion";
import {
  IconAlertCircle,
  IconBrackets,
  IconCode,
  IconDatabase,
  IconForms,
  IconGlobe,
  IconInfoCircle,
  IconLayout,
  IconListDetails,
  IconPalette,
  IconUpload,
} from "@tabler/icons-react";
import { PageHeader } from "../../components/PageHeader";
import { useOrganizationLimits } from "../../../../business/limits.hook";
import { LimitUsageBar } from "../../components/LimitIndicator";

const useStyles = createStyles((theme) => ({
  section: {
    marginBottom: theme.spacing.xl,
  },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.sm,
    marginBottom: theme.spacing.md,
  },
  card: {
    backgroundColor: theme.colors.dark[7],
    border: `1px solid ${theme.colors.dark[5]}`,
  },
  codeBlock: {
    backgroundColor: theme.colors.dark[8],
    border: `1px solid ${theme.colors.dark[5]}`,
    borderRadius: theme.radius.md,
    padding: theme.spacing.md,
    fontFamily: "monospace",
    fontSize: theme.fontSizes.sm,
    lineHeight: 1.6,
    overflowX: "auto" as const,
    whiteSpace: "pre" as const,
    color: theme.colors.dark[0],
  },
  mutationBadge: {
    fontFamily: "monospace",
    textTransform: "none" as const,
    fontWeight: 600,
  },
  flowStep: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing.sm,
    padding: theme.spacing.sm,
    borderLeft: `2px solid ${theme.colors.cyan[7]}`,
    marginLeft: rem(10),
    "&:last-of-type": {
      borderLeft: "none",
    },
  },
  statusTable: {
    "& td, & th": {
      padding: `${rem(8)} ${rem(12)}`,
    },
  },
}));

const GqlBlock = ({ children }: { children: string }) => {
  const { classes } = useStyles();
  return <div className={classes.codeBlock}>{children.trim()}</div>;
};

const SectionHeader = ({
  icon,
  title,
  id,
}: {
  icon: React.ReactNode;
  title: string;
  id: string;
}) => {
  const { classes } = useStyles();
  return (
    <div className={classes.sectionTitle} id={id}>
      <ThemeIcon size="md" radius="md" variant="light" color="cyan">
        {icon}
      </ThemeIcon>
      <Title order={2}>{title}</Title>
    </div>
  );
};

export const PlatformHome = () => {
  const { classes } = useStyles();
  const { getLimit, loading: limitsLoading } = useOrganizationLimits([
    "PLATFORMS",
    "PLATFORM_TEMPLATES",
    "ORGANIZATION_STORAGE_SIZE",
  ]);
  const platformsLimit = getLimit("PLATFORMS");
  const templatesLimit = getLimit("PLATFORM_TEMPLATES");
  const storageLimit = getLimit("ORGANIZATION_STORAGE_SIZE");

  const activeLimits = [platformsLimit, templatesLimit, storageLimit].filter(
    Boolean,
  );

  return (
    <div className="wp-fade-in">
      <PageHeader
        title="Platform Documentation"
        subtitle="Complete reference for the website builder and hosting system"
        showBreadcrumbs={false}
      />

      {!limitsLoading && activeLimits.length > 0 && (
        <SimpleGrid cols={{ base: 1, md: 3 }} mb="lg">
          {platformsLimit && (
            <LimitUsageBar limit={platformsLimit} label="Platforms" />
          )}
          {templatesLimit && (
            <LimitUsageBar limit={templatesLimit} label="Templates" />
          )}
          {storageLimit && (
            <LimitUsageBar limit={storageLimit} label="Storage" />
          )}
        </SimpleGrid>
      )}

      <Group gap="xs" mb="xl">
        <Badge color="orange" size="lg">
          PREVIEW
        </Badge>
        <Badge color="red" size="lg">
          UNSTABLE
        </Badge>
        <Text size="sm" c="dimmed">
          This feature is in preview and may contain breaking changes.
        </Text>
      </Group>

      {/* Table of Contents */}
      <Card className={classes.card} p="md" radius="md" mb="xl">
        <Text fw={600} mb="sm">
          Table of Contents
        </Text>
        <SimpleGrid cols={{ base: 1, md: 3 }}>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#overview">Overview</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#getting-started">Getting Started</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#files">Files & CDN</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#liquid">Liquid Templating</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#forms">Forms & Mutations</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#domains">Custom Domains</Anchor>
            </List.Item>
          </List>
          <List spacing={4} size="sm">
            <List.Item>
              <Anchor href="#templates">Templates</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#graphql">GraphQL API Reference</Anchor>
            </List.Item>
            <List.Item>
              <Anchor href="#data-model">Data Model</Anchor>
            </List.Item>
          </List>
        </SimpleGrid>
      </Card>

      <Stack gap="xl">
        {/* ===================== OVERVIEW ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconLayout size={18} />}
            title="Overview"
            id="overview"
          />
          <Text mb="md">
            The Platform service allows you to create fully custom gaming
            websites hosted on WellPlayed infrastructure. Platforms use the{" "}
            <strong>Liquid templating engine</strong> (compatible with
            Shopify/Jekyll) to build dynamic, data-driven pages that integrate
            directly with WellPlayed's tournament, player, and gamification
            APIs.
          </Text>
          <Text mb="md">
            A platform is composed of a hierarchy of entities:
          </Text>
          <Card className={classes.card} p="md" radius="md">
            <GqlBlock>
              {`Platform
├── Configuration (whitelabel settings)
├── Custom Domains[] (custom domain mappings with SSL)
├── Files[] (pages, components, images, stylesheets)
│   ├── PAGE — Routable Liquid pages with URL paths
│   ├── COMPONENT — Reusable Liquid partials
│   ├── IMAGE — Image assets (PNG, JPG, SVG, etc.)
│   └── TEXT — Generic text files (CSS, JS, JSON, etc.)
└── Templates[] (pre-built designs for quick setup)`}
            </GqlBlock>
          </Card>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            <Text size="sm">
              <strong>Alternative approaches:</strong> If you prefer building
              with React, you can use the{" "}
              <Anchor
                href="https://www.npmjs.com/package/@well-played.gg/react-sdk"
                target="_blank"
              >
                @well-played.gg/react-sdk
              </Anchor>{" "}
              package, or build your own frontend using the GraphQL API
              directly.
            </Text>
          </Alert>
        </div>

        {/* ===================== GETTING STARTED ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconListDetails size={18} />}
            title="Getting Started"
            id="getting-started"
          />
          <Text mb="md">
            Setting up a platform is a multi-step process. First you create the
            platform with a subdomain, then populate it with files and configure
            your custom domain.
          </Text>

          <Title order={4} mb="xs">
            Step 1: Create the Platform
          </Title>
          <Text mb="sm">
            Navigate to <Code>/platform/new</Code>. Required fields:
          </Text>
          <List spacing="xs" mb="md">
            <List.Item>
              <strong>Name</strong> — Display name for your platform (string,
              required)
            </List.Item>
            <List.Item>
              <strong>Subdomain</strong> — Your platform will be available at{" "}
              <Code>{"<subdomain>.well-played.gg"}</Code> (string, required,
              must be unique)
            </List.Item>
          </List>
          <Text mb="md" size="sm" c="dimmed">
            Optional: Link an <Code>appId</Code> for OIDC authentication and
            enable <Code>whitelabel</Code> mode to remove WellPlayed branding.
          </Text>

          <GqlBlock>
            {`mutation ($name: String!, $subdomain: String!) {
  platformCreate(input: {
    name: $name
    subdomain: $subdomain
    configuration: { whitelabel: false }
  }) {
    id
  }
}`}
          </GqlBlock>

          <Title order={4} mt="lg" mb="xs">
            Step 2: Create Files
          </Title>
          <Text mb="sm">
            Navigate to <Code>Files</Code> and create your first page. Choose
            from four file types:
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                PAGE
              </Text>
              <Text size="sm">
                Routable Liquid pages. Each page has a URL path (e.g.{" "}
                <Code>/tournaments/:tournamentId</Code>). Supports route
                parameters accessible via{" "}
                <Code>{"{{ params.tournamentId }}"}</Code>.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                COMPONENT
              </Text>
              <Text size="sm">
                Reusable Liquid partials that can be included in pages. Use
                these for headers, footers, navigation, and other shared UI
                elements.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                IMAGE
              </Text>
              <Text size="sm">
                Image assets uploaded via drag-and-drop. Images are hosted on
                the WellPlayed CDN and can be referenced from pages and
                stylesheets.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs">
                TEXT
              </Text>
              <Text size="sm">
                Generic text files: CSS stylesheets, JavaScript files, JSON
                data, etc. Edited with the Monaco code editor with syntax
                highlighting.
              </Text>
            </Card>
          </SimpleGrid>

          <Title order={4} mt="lg" mb="xs">
            Step 3: Edit & Publish
          </Title>
          <Text mb="sm">Each file has two CDN versions:</Text>
          <List spacing="xs" mb="md">
            <List.Item>
              <Badge color="yellow" variant="light" size="sm" mr="xs">
                dev
              </Badge>
              Draft version — auto-updates on save, used for live preview
            </List.Item>
            <List.Item>
              <Badge color="green" variant="light" size="sm" mr="xs">
                prod
              </Badge>
              Published version — requires explicit publish action, served to
              end users
            </List.Item>
          </List>
          <Text mb="md" size="sm" c="dimmed">
            Use the Hot Reload debug window to preview changes in real-time
            before publishing.
          </Text>

          <Title order={4} mt="lg" mb="xs">
            Step 4: Configure Custom Domain (Optional)
          </Title>
          <Text mb="sm">
            Navigate to <Code>Domains</Code> to add a custom domain. See the{" "}
            <Anchor href="#domains">Custom Domains</Anchor> section for detailed
            setup instructions.
          </Text>
        </div>

        {/* ===================== FILES & CDN ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconUpload size={18} />}
            title="Files & CDN"
            id="files"
          />
          <Text mb="md">
            Platform files are stored on WellPlayed's CDN with a two-tier
            system: a dev (draft) CDN for editing and previewing, and a prod CDN
            for the live site. This allows safe iteration without affecting end
            users.
          </Text>

          <Title order={4} mb="xs">
            File Upload Workflow
          </Title>
          <Text mb="sm">
            For text-based files (PAGE, COMPONENT, TEXT), content is saved
            directly. For images, a multi-step upload process is used:
          </Text>

          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`1. Create file         → platformFileCreate(input, platformId)
2. Get upload URL      → platformFileGenerateUploadUrl(id, size)
3. Upload to S3        → PUT to presigned URL
4. Validate upload     → platformFileValidateFileUpload(id)
5. File on dev CDN     → accessible via devCdnPath
6. Publish to prod     → platformFilePublish(id) → accessible via prodCdnPath`}
            </GqlBlock>
          </Card>

          <Title order={4} mt="lg" mb="xs">
            Page Configuration
          </Title>
          <Text mb="sm">
            PAGE files have a <Code>config.page.path</Code> field that defines
            the URL route. Paths support dynamic segments:
          </Text>
          <List spacing="xs" mb="md">
            <List.Item>
              <Code>/</Code> — Homepage
            </List.Item>
            <List.Item>
              <Code>/tournaments</Code> — Static path
            </List.Item>
            <List.Item>
              <Code>/tournaments/:tournamentId</Code> — Dynamic segment,
              accessible as <Code>{"{{ params.tournamentId }}"}</Code>
            </List.Item>
          </List>

          <Alert icon={<IconInfoCircle />} color="indigo" variant="light">
            The file editor supports hot reload: open a debug window that
            connects to your platform with <Code>?hotUpdates</Code> enabled.
            Changes are pushed in real-time via <Code>postMessage</Code>.
          </Alert>
        </div>

        {/* ===================== LIQUID TEMPLATING ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconCode size={18} />}
            title="Liquid Templating"
            id="liquid"
          />
          <Text mb="md">
            Platform pages use the{" "}
            <Anchor href="https://shopify.github.io/liquid/" target="_blank">
              Liquid
            </Anchor>{" "}
            templating engine. If you've used Shopify themes, Jekyll, or GitHub
            Pages, the syntax will be familiar.
          </Text>

          <Title order={4} mb="xs">
            Outputs
          </Title>
          <Text mb="sm" size="sm">
            Display variables using double curly braces. Filters transform
            values:
          </Text>
          <GqlBlock>
            {`{{ username }}
{{ username | append: ", welcome!" | capitalize }}
{{ tournament.startAt | date: "%B %d, %Y" }}`}
          </GqlBlock>

          <Title order={4} mt="lg" mb="xs">
            Tags
          </Title>
          <Text mb="sm" size="sm">
            Control logic and assign variables using curly-percent blocks:
          </Text>
          <GqlBlock>
            {`{% assign greeting = "Hello" %}
{% if user.role == "admin" %}
  <p>Welcome, admin!</p>
{% elsif user.role == "player" %}
  <p>Welcome, player!</p>
{% else %}
  <p>Welcome!</p>
{% endif %}

{% for tournament in tournaments %}
  <div>{{ tournament.title }}</div>
{% endfor %}`}
          </GqlBlock>

          <Divider my="lg" />

          <Title order={3} mb="md">
            Custom Tags Reference
          </Title>

          <Title order={4} mb="xs">
            Meta Tags
          </Title>
          <SimpleGrid cols={{ base: 1, md: 3 }} mb="md">
            <Card className={classes.card} p="sm" radius="md">
              <Code>{'{% favicon "icon.svg" %}'}</Code>
              <Text size="xs" c="dimmed" mt={4}>
                Set the page favicon
              </Text>
            </Card>
            <Card className={classes.card} p="sm" radius="md">
              <Code>{'{% title "My Platform" %}'}</Code>
              <Text size="xs" c="dimmed" mt={4}>
                Set the page title
              </Text>
            </Card>
            <Card className={classes.card} p="sm" radius="md">
              <Code>{'{% stylesheet "styles.css" %}'}</Code>
              <Text size="xs" c="dimmed" mt={4}>
                Inject a CSS stylesheet
              </Text>
            </Card>
          </SimpleGrid>

          <Title order={4} mb="xs">
            Navigation Tags
          </Title>
          <GqlBlock>
            {`{# Internal link (SPA navigation, no page reload) #}
{% nav-link to:"/tournaments/123" %}
  View Tournament
{% end-nav-link %}

{# Programmatic redirect #}
{% navigate "/home" %}`}
          </GqlBlock>

          <Title order={4} mt="lg" mb="xs">
            Data Fetching: api-query
          </Title>
          <Text mb="sm" size="sm">
            Fetch data from the WellPlayed GraphQL API directly in your
            templates. The tag exposes <Code>result</Code> and{" "}
            <Code>error</Code> variables within its scope:
          </Text>
          <GqlBlock>
            {`{% capture query %}
query tournaments($page: PageInfo!) {
  tournaments(query: { status: "ALL" }, page: $page) {
    nodes { id, title, startAt }
    totalCount
  }
}
{% endcapture %}

{% capture variables %}
{ "page": { "first": 10 } }
{% endcapture %}

{% api-query query:query variables:variables %}
  {% if error %}
    <p class="error">{{ error }}</p>
  {% else %}
    <ul>
      {% for tournament in result.tournaments.nodes %}
        <li>{{ tournament.title }} - {{ tournament.startAt | date: "%B %d" }}</li>
      {% endfor %}
    </ul>
  {% endif %}
{% end-api-query %}`}
          </GqlBlock>

          <Title order={4} mt="lg" mb="xs">
            Data Fetching: api-mutation
          </Title>
          <Text mb="sm" size="sm">
            Execute GraphQL mutations from templates. Same scope outputs as{" "}
            <Code>api-query</Code>:
          </Text>
          <GqlBlock>
            {`{% capture mutation %}
mutation registerTeam($name: String!, $tag: String!) {
  registerTournamentTeam(
    tournamentId: "{{ params.tournamentId }}"
    input: { name: $name, tag: $tag }
  ) { id, name }
}
{% endcapture %}

{% capture variables %}
{ "name": "Team Alpha", "tag": "ALPHA" }
{% endcapture %}

{% api-mutation mutation:mutation variables:variables %}
  {% if error %}
    <p>Registration failed: {{ error }}</p>
  {% else %}
    <p>Team registered: {{ result.registerTournamentTeam.name }}</p>
  {% endif %}
{% end-api-mutation %}`}
          </GqlBlock>

          <Title order={4} mt="lg" mb="xs">
            Shorthand Data Tags
          </Title>
          <Text mb="sm" size="sm">
            Convenience tags for common tournament data:
          </Text>
          <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs" size="sm">
                tournament-teams
              </Text>
              <GqlBlock>
                {`{% tournament-teams tournament-id:"123" status:"CONFIRMED" %}
  {% for team in result %}
    <div>{{ team.name }} ({{ team.tag }})</div>
  {% endfor %}
{% end-tournament-teams %}`}
              </GqlBlock>
              <Text size="xs" c="dimmed" mt="xs">
                Optional <Code>status</Code> filter: REGISTERED, CONFIRMED,
                DENIED, etc.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs" size="sm">
                tournament-step
              </Text>
              <GqlBlock>
                {`{% tournament-step tournament-id:"123" step-id:"456" %}
  {% for group in result %}
    <h3>{{ group.name }}</h3>
    {# Render rounds, games, etc. #}
  {% endfor %}
{% end-tournament-step %}`}
              </GqlBlock>
              <Text size="xs" c="dimmed" mt="xs">
                Fetches groups for a tournament step.
              </Text>
            </Card>
          </SimpleGrid>

          <Title order={4} mt="lg" mb="xs">
            Content Tags
          </Title>
          <GqlBlock>
            {`{# Render rich text HTML content (from TipTap editor) #}
{% rich-text content:tournament.description %}

{# Inject JavaScript #}
{% javascript %}
  document.addEventListener('DOMContentLoaded', () => {
    console.log('Page loaded!');
  });
{% end-javascript %}`}
          </GqlBlock>
        </div>

        {/* ===================== FORMS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconForms size={18} />}
            title="Forms & Mutations"
            id="forms"
          />
          <Text mb="md">
            The <Code>form</Code> tag creates HTML forms that automatically
            execute GraphQL mutations on submit. Input <Code>name</Code>{" "}
            attributes are mapped to mutation variables using JSON path
            notation.
          </Text>

          <Title order={4} mb="xs">
            Form Syntax
          </Title>
          <GqlBlock>
            {`{% if formResult %}
  {% if formResult.error %}
    <p class="error">{{ formResult.error }}</p>
  {% else %}
    <p>Registration successful!</p>
  {% endif %}
{% else %}
  {% capture mutation %}
  mutation registerTeam($teamName: String!, $teamTag: String!) {
    registerTournamentTeam(
      tournamentId: "{{ params.tournamentId }}"
      input: { name: $teamName, tag: $teamTag }
    ) { id }
  }
  {% endcapture %}

  {% form mutation:mutation output-variable:"formResult" %}
    <input type="text" name="teamName" placeholder="Team Name" required />
    <input type="text" name="teamTag" placeholder="TAG" required />
    <button type="submit">Register</button>
  {% endform %}
{% endif %}`}
          </GqlBlock>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
            mb="md"
          >
            <Text size="sm">
              Input names use JSON path mapping: a name like{" "}
              <Code>user.name</Code> becomes{" "}
              <Code>{"{ user: { name: value } }"}</Code>. Arrays use bracket
              notation: <Code>customFields[0].property</Code>.
            </Text>
          </Alert>

          <Title order={4} mt="lg" mb="xs">
            Pre-defined Form Templates
          </Title>
          <Text mb="sm" size="sm">
            These form definitions are available as starting points:
          </Text>
          <Table className={classes.statusTable} mb="md">
            <thead>
              <tr>
                <th>Tag</th>
                <th>Description</th>
                <th>Fields</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <Code>update-local-player-profile</Code>
                </td>
                <td>Update the logged-in player's profile</td>
                <td>username, description</td>
              </tr>
              <tr>
                <td>
                  <Code>register-tournament-team</Code>
                </td>
                <td>Register a team to a tournament</td>
                <td>name, tag</td>
              </tr>
              <tr>
                <td>
                  <Code>confirm-tournament-team</Code>
                </td>
                <td>Confirm a team's status</td>
                <td>status, team-id</td>
              </tr>
              <tr>
                <td>
                  <Code>confirm-tournament-attendance</Code>
                </td>
                <td>Confirm team attendance</td>
                <td>attendance, tournament-id</td>
              </tr>
              <tr>
                <td>
                  <Code>delete-tournament-team</Code>
                </td>
                <td>Delete a team from a tournament</td>
                <td>tournament-id</td>
              </tr>
            </tbody>
          </Table>
        </div>

        {/* ===================== CUSTOM DOMAINS ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconGlobe size={18} />}
            title="Custom Domains"
            id="domains"
          />
          <Text mb="md">
            Custom domains allow you to serve your platform on your own domain
            (e.g. <Code>play.yourbrand.com</Code>) with automatic SSL
            certificate provisioning.
          </Text>

          <Title order={4} mb="xs">
            Setup Process
          </Title>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`1. Register domain     → platformRegisterCustomDomain(platformId, hostname)
2. Add CNAME record    → Point your domain to cname.well-played.gg
3. Add TXT records     → Ownership verification (provided by the API)
4. Add SSL TXT records → SSL certificate validation (provided by the API)
5. Wait for validation → Status moves from "pending" → "active"
6. Force re-check      → platformForceCustomDomainRecheck(hostname)`}
            </GqlBlock>
          </Card>

          <Title order={4} mt="lg" mb="xs">
            Domain Status Flow
          </Title>
          <Card className={classes.card} p="md" radius="md" mb="md">
            <GqlBlock>
              {`pending ──→ active
   │           │
   │           ├──→ active_redeploying ──→ active
   │           │
   │           └──→ moved
   │
   ├──→ pending_provisioned ──→ active
   │
   ├──→ blocked / pending_blocked
   │
   └──→ pending_deletion ──→ deleted`}
            </GqlBlock>
          </Card>

          <Title order={4} mt="lg" mb="xs">
            SSL Validation
          </Title>
          <Text mb="sm" size="sm">
            SSL certificates are automatically provisioned via Let's Encrypt
            once DNS records are validated. The API provides the required TXT
            records. SSL status transitions:
          </Text>
          <List spacing="xs" mb="md">
            <List.Item>
              <Badge color="yellow" variant="light" size="sm" mr="xs">
                pending_validation
              </Badge>
              DNS records not yet verified
            </List.Item>
            <List.Item>
              <Badge color="green" variant="light" size="sm" mr="xs">
                active
              </Badge>
              Certificate issued and active
            </List.Item>
          </List>

          <Alert icon={<IconAlertCircle />} color="yellow" variant="light">
            DNS propagation can take several minutes. Use{" "}
            <Code>platformForceCustomDomainRecheck</Code> sparingly — the system
            automatically polls for changes.
          </Alert>
        </div>

        {/* ===================== TEMPLATES ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconPalette size={18} />}
            title="Templates"
            id="templates"
          />
          <Text mb="md">
            Platform templates are pre-built designs that can be applied to a
            platform to quickly set up pages, components, and styling. Templates
            can be public (marketplace) or private (organization-only).
          </Text>

          <Title order={4} mb="xs">
            Page Templates
          </Title>
          <Text mb="sm" size="sm">
            When creating a new PAGE file, you can start from a template:
          </Text>
          <SimpleGrid cols={{ base: 1, md: 3 }} mb="md">
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs" size="sm">
                Basic Tournaments List
              </Text>
              <Text size="xs" c="dimmed">
                Lists tournaments using <Code>api-query</Code> with pagination.
                A good starting point for a tournaments overview page.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs" size="sm">
                Basic Tournament Page
              </Text>
              <Text size="xs" c="dimmed">
                Tournament detail page using route parameters. Displays
                tournament info, teams, and bracket data.
              </Text>
            </Card>
            <Card className={classes.card} p="md" radius="md">
              <Text fw={600} mb="xs" size="sm">
                Full Homepage
              </Text>
              <Text size="xs" c="dimmed">
                Complete gaming homepage with header, hero section, features
                grid, and footer. Based on gaming theme styles.
              </Text>
            </Card>
          </SimpleGrid>

          <Title order={4} mt="lg" mb="xs">
            Platform Templates
          </Title>
          <Text mb="sm" size="sm">
            Full platform templates include all files (pages, components,
            styles) needed for a complete website. Apply with{" "}
            <Code>platformTemplateApply</Code>:
          </Text>
          <GqlBlock>
            {`mutation ($platformId: ID!, $templateId: ID!) {
  platformTemplateApply(
    platformId: $platformId
    platformTemplateId: $templateId
  ) {
    id, name
  }
}`}
          </GqlBlock>

          <Alert
            icon={<IconInfoCircle />}
            color="indigo"
            variant="light"
            mt="md"
          >
            The template marketplace is coming soon. You will be able to browse,
            create, and share platform templates publicly.
          </Alert>
        </div>

        {/* ===================== GRAPHQL API ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconBrackets size={18} />}
            title="GraphQL API Reference"
            id="graphql"
          />
          <Text mb="md">
            Complete reference for all platform-related GraphQL operations.
            Operations marked with{" "}
            <Badge color="cyan" size="xs" className={classes.mutationBadge}>
              QUERY
            </Badge>{" "}
            are read operations,{" "}
            <Badge color="orange" size="xs" className={classes.mutationBadge}>
              MUTATION
            </Badge>{" "}
            are write operations.
          </Text>

          <Accordion variant="separated" radius="md" mb="md">
            <Accordion.Item value="platform-queries">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="cyan" className={classes.mutationBadge}>
                    QUERIES
                  </Badge>
                  <Text>Platform Queries</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      platforms
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      List all platforms for the current organization.
                    </Text>
                    <GqlBlock>
                      {`query {
  platforms {
    id, name, organizationId, appId
    configuration { whitelabel }
    customDomains { domain, configuration { hostname, status } }
    createdAt, updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platform
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Get a single platform by ID.
                    </Text>
                    <GqlBlock>
                      {`query ($platformId: ID!) {
  platform(platformId: $platformId) {
    id, name, organizationId, appId
    configuration { whitelabel }
    customDomains {
      domain
      configuration {
        hostname, status
        ssl { status, validation_records { txt_name, txt_value } }
        ownership_verification { name, type, value }
      }
    }
    createdAt, updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformFindFromDomain
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Look up a platform by its domain name. Used internally by
                      the platform renderer to resolve which platform to serve.
                    </Text>
                    <GqlBlock>
                      {`query ($domain: String!) {
  platformFindFromDomain(domain: $domain) {
    id, name
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformCheckDomainAvailability
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Check if a subdomain is available before creating a
                      platform.
                    </Text>
                    <GqlBlock>
                      {`query ($domain: URL!) {
  platformCheckDomainAvailability(domain: $domain)
  # Returns: Boolean!
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="platform-mutations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="orange" className={classes.mutationBadge}>
                    MUTATIONS
                  </Badge>
                  <Text>Platform Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      platformCreate
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Create a new platform with a unique subdomain.
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: CreatePlatformInput!) {
  platformCreate(input: $input) {
    id, name
  }
}

input CreatePlatformInput {
  name: String!
  subdomain: String!
  appId: String
  configuration: PlatformConfigurationInput
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformUpdate
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Update platform name, appId, or configuration.
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $input: UpdatePlatformInput!) {
  platformUpdate(id: $id, input: $input) {
    id, name
  }
}

input UpdatePlatformInput {
  name: String!
  appId: String
  configuration: PlatformConfigurationInput
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformDelete
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Permanently delete a platform and all its files.
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) {
  platformDelete(id: $id)
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="file-queries">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="cyan" className={classes.mutationBadge}>
                    QUERIES
                  </Badge>
                  <Text>File Queries</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      platformFile
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Get a single file with its contents and CDN paths.
                    </Text>
                    <GqlBlock>
                      {`query ($id: ID!) {
  platformFile(id: $id) {
    id, name, type, contents
    config { page { path } }
    devCdnPath, prodCdnPath
    size, createdAt, updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformFiles
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      List files for a platform or template. Optionally filter
                      by file type.
                    </Text>
                    <GqlBlock>
                      {`query ($platformId: ID, $type: PlatformCdnFileType) {
  platformFiles(platformId: $platformId, type: $type) {
    id, name, type
    config { page { path } }
    devCdnPath, prodCdnPath
    size, createdAt, updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="file-mutations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="orange" className={classes.mutationBadge}>
                    MUTATIONS
                  </Badge>
                  <Text>File Mutations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      platformFileCreate
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Create a new file in a platform or template.
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: PlatformCdnFileCreateInput!, $platformId: ID) {
  platformFileCreate(input: $input, platformId: $platformId) {
    id, name, type, devCdnPath
  }
}

input PlatformCdnFileCreateInput {
  name: String!
  type: PlatformCdnFileType!
  config: PlatformCdnFileConfigsInput
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformFileUpdate
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Update file metadata (name, config). Does not update file
                      contents — use the upload flow for that.
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $input: PlatformFileUpdateInput!) {
  platformFileUpdate(id: $id, input: $input) {
    id, name, type
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformFileGenerateUploadUrl
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Generate a presigned S3 URL for uploading file contents.
                      Returns a URL and headers to use with a PUT request.
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $size: Float!) {
  platformFileGenerateUploadUrl(id: $id, size: $size) {
    url
    headers { key, value }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformFileValidateFileUpload
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Validate that a file was successfully uploaded to S3. Must
                      be called after uploading via the presigned URL.
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) {
  platformFileValidateFileUpload(id: $id) {
    id, devCdnPath, size
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformFilePublish
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Publish a file from the dev CDN to the production CDN.
                      Makes the file available to end users.
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) {
  platformFilePublish(id: $id) {
    id, prodCdnPath
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformFileDelete
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Permanently delete a file from the platform.
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) {
  platformFileDelete(id: $id)
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="domain-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="orange" className={classes.mutationBadge}>
                    DOMAINS
                  </Badge>
                  <Text>Custom Domain Operations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      platformCustomDomains
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      List all custom domains for a platform with their
                      validation status.
                    </Text>
                    <GqlBlock>
                      {`query ($platformId: ID!) {
  platformCustomDomains(platformId: $platformId) {
    domain
    configuration {
      hostname, status
      ssl {
        status
        validation_records { txt_name, txt_value }
        validation_errors { message }
      }
      ownership_verification { name, type, value }
      verification_errors
    }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformRegisterCustomDomain
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Register a new custom domain for a platform. Returns DNS
                      records that must be configured.
                    </Text>
                    <GqlBlock>
                      {`mutation ($platformId: ID!, $hostname: String!) {
  platformRegisterCustomDomain(
    platformId: $platformId
    hostname: $hostname
  ) {
    hostname, status
    ssl { status, validation_records { txt_name, txt_value } }
    ownership_verification { name, type, value }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformForceCustomDomainRecheck
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Force a re-validation check on a custom domain's DNS and
                      SSL configuration.
                    </Text>
                    <GqlBlock>
                      {`mutation ($hostname: String!) {
  platformForceCustomDomainRecheck(hostname: $hostname) {
    hostname, status
    ssl { status }
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformRemoveCustomDomain
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Remove a custom domain from a platform.
                    </Text>
                    <GqlBlock>
                      {`mutation ($hostname: String!) {
  platformRemoveCustomDomain(hostname: $hostname)
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="template-operations">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="gray" className={classes.mutationBadge}>
                    TEMPLATES
                  </Badge>
                  <Text>Template Operations</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      platformTemplates
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      List all templates for the current organization.
                    </Text>
                    <GqlBlock>
                      {`query {
  platformTemplates {
    id, name, description, public
    screenshotUrl, createdAt, updatedAt
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformTemplatesPublic
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Browse public templates available in the marketplace.
                    </Text>
                    <GqlBlock>
                      {`query {
  platformTemplatesPublic {
    id, name, description, screenshotUrl
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformTemplate
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Get a single template by ID.
                    </Text>
                    <GqlBlock>
                      {`query ($platformTemplateId: ID!) {
  platformTemplate(platformTemplateId: $platformTemplateId) {
    id, name, description, public, screenshotUrl
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformTemplateCreate
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Create a new template from scratch.
                    </Text>
                    <GqlBlock>
                      {`mutation ($input: CreatePlatformTemplateInput!) {
  platformTemplateCreate(input: $input) {
    id, name
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformTemplateUpdate
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Update a template's name, description, or visibility.
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!, $input: UpdatePlatformTemplateInput!) {
  platformTemplateUpdate(id: $id, input: $input) {
    id, name
  }
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformTemplateDelete
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Delete a template.
                    </Text>
                    <GqlBlock>
                      {`mutation ($id: ID!) {
  platformTemplateDelete(id: $id)
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      platformTemplateApply
                    </Text>
                    <Text size="sm" c="dimmed" mb="xs">
                      Apply a template to a platform, importing all its files
                      and configuration.
                    </Text>
                    <GqlBlock>
                      {`mutation ($platformId: ID!, $platformTemplateId: ID!) {
  platformTemplateApply(
    platformId: $platformId
    platformTemplateId: $platformTemplateId
  ) {
    id, name
  }
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="input-types">
              <Accordion.Control>
                <Group gap="xs">
                  <Badge color="gray" className={classes.mutationBadge}>
                    INPUTS
                  </Badge>
                  <Text>Input Types Reference</Text>
                </Group>
              </Accordion.Control>
              <Accordion.Panel>
                <Stack gap="md">
                  <div>
                    <Text fw={600} mb="xs">
                      Platform Inputs
                    </Text>
                    <GqlBlock>
                      {`input CreatePlatformInput {
  name: String!
  subdomain: String!
  appId: String
  configuration: PlatformConfigurationInput
}

input UpdatePlatformInput {
  name: String!
  appId: String
  configuration: PlatformConfigurationInput
}

input PlatformConfigurationInput {
  whitelabel: Boolean
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      File Inputs
                    </Text>
                    <GqlBlock>
                      {`input PlatformCdnFileCreateInput {
  name: String!
  type: PlatformCdnFileType!
  config: PlatformCdnFileConfigsInput
}

input PlatformCdnFileConfigsInput {
  page: PlatformCdnFilePageConfigInput
}

input PlatformCdnFilePageConfigInput {
  path: String!
}

input PlatformFileUpdateInput {
  name: String
  config: PlatformCdnFileConfigsInput
}`}
                    </GqlBlock>
                  </div>
                  <Divider />
                  <div>
                    <Text fw={600} mb="xs">
                      Template Inputs
                    </Text>
                    <GqlBlock>
                      {`input CreatePlatformTemplateInput {
  name: String!
  description: String!
  public: Boolean!
  screenshotUrl: URL!
}

input UpdatePlatformTemplateInput {
  name: String
  description: String
  public: Boolean
  screenshotUrl: URL
}`}
                    </GqlBlock>
                  </div>
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* ===================== DATA MODEL ===================== */}
        <div className={classes.section}>
          <SectionHeader
            icon={<IconDatabase size={18} />}
            title="Data Model"
            id="data-model"
          />
          <Text mb="md">
            Entity hierarchy and key types in the Platform system.
          </Text>

          <Accordion variant="separated" radius="md">
            <Accordion.Item value="entities">
              <Accordion.Control>Entity Hierarchy</Accordion.Control>
              <Accordion.Panel>
                <GqlBlock>
                  {`Platform
├── id: ID!
├── name: String!
├── organizationId: ID!
├── appId: String
├── configuration: PlatformConfiguration
│   └── whitelabel: Boolean
├── customDomains: [PlatformCustomDomainData]
│   ├── domain: String!
│   └── configuration: CustomDomainData
│       ├── hostname: String!
│       ├── status: CustomDomainStatus
│       ├── ssl: CustomDomainSsl
│       │   ├── status: CustomDomainValidationStatus
│       │   ├── validation_records: [{ txt_name, txt_value }]
│       │   └── validation_errors: [{ message }]
│       ├── ownership_verification: { name, type, value }
│       └── verification_errors: [String]
├── createdAt: DateTime!
└── updatedAt: DateTime!

PlatformCdnFile
├── id: ID!
├── name: String!
├── type: PlatformCdnFileType!
├── platformId: ID
├── platformTemplateId: ID
├── config: PlatformCdnFileConfigs
│   └── page: PlatformCdnFilePageConfig
│       └── path: String!
├── contents: String
├── devCdnPath: String
├── prodCdnPath: String
├── size: Float!
├── createdAt: DateTime!
└── updatedAt: DateTime!

PlatformTemplate
├── id: ID!
├── name: String!
├── description: String!
├── organizationId: ID!
├── ownerId: ID!
├── public: Boolean!
├── screenshotUrl: URL!
├── createdAt: DateTime!
└── updatedAt: DateTime!`}
                </GqlBlock>
              </Accordion.Panel>
            </Accordion.Item>

            <Accordion.Item value="enums">
              <Accordion.Control>Enums</Accordion.Control>
              <Accordion.Panel>
                <SimpleGrid cols={{ base: 1, md: 2 }}>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      PlatformCdnFileType
                    </Text>
                    <GqlBlock>
                      {`PAGE        # Routable Liquid page
COMPONENT   # Reusable Liquid partial
IMAGE       # Image asset
TEXT        # Generic text file (CSS, JS, etc.)`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      CustomDomainStatus
                    </Text>
                    <GqlBlock>
                      {`pending
pending_provisioned
active
active_redeploying
pending_deletion
deleted
blocked
pending_blocked
moved
pending_migration`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      CustomDomainValidationStatus
                    </Text>
                    <GqlBlock>
                      {`initializing
pending_validation
pending_issuance
pending_deployment
pending_deletion
active
deleted
deactivating
deactivated`}
                    </GqlBlock>
                  </div>
                  <div>
                    <Text fw={600} size="sm" mb="xs">
                      Liquid Custom Tags
                    </Text>
                    <GqlBlock>
                      {`favicon          # Set page favicon
title            # Set page title
stylesheet       # Inject CSS
nav-link         # Internal SPA link
navigate         # Programmatic redirect
javascript       # Inject JS
api-query        # GraphQL query
api-mutation     # GraphQL mutation
tournament-teams # Fetch teams
tournament-step  # Fetch step groups
rich-text        # Render HTML content
form             # Mutation form`}
                    </GqlBlock>
                  </div>
                </SimpleGrid>
              </Accordion.Panel>
            </Accordion.Item>
          </Accordion>
        </div>
      </Stack>
    </div>
  );
};
